/**
 * File for the `Customer assistance tickets` widget form.
 */

import { Col, Divider, Input, Row, Select } from 'antd';
import FormItem from 'antd/lib/form/FormItem';
import { filter, get, isEmpty, map, toLower } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { CUSTOM_FIELD_TYPES } from '../../config/tableAndPageConstants';
import { ATBStateOptions } from '../../constants/customersSortAndFilters';
import { getCustomerUILabel } from '../../store/customers/sagas';
import { populatePopoverContainer } from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';
import { customFieldIndicator } from '../common/FilterBar';
import SelectReadonly from '../FormComponents/SelectReadonly';
import OrganisationWidgetCommonFilters from './organisation/OrganisationWidgetCommonFilters';

const { Option } = Select;

interface IProps {
    widgetDetails: DynamicObject;
    getFieldDecorator: any;
    drawerRef: any;
    formDisabled?: boolean;
    customFieldsFilterList: DynamicObject[];
    isOrgView?: boolean;
    setFieldsValue: any;
    getFieldValue: any;
}

const OpenSupportTicketsWidgetFields: React.FC<IProps> = ({
    widgetDetails,
    getFieldDecorator,
    drawerRef,
    isOrgView,
    getFieldValue,
    setFieldsValue,
    customFieldsFilterList,
    formDisabled,
}) => {
    const customerLabel = useSelector(getCustomerUILabel);

    /**
     * Function for populating the state select options.
     */
    const populateInvoiceStateSelectOptions = () =>
        map(ATBStateOptions, ({ label, value }: any) => (
            <Option key={value} value={value}>
                {label}
            </Option>
        ));

    const populateCustomFieldsFilters = (customFieldType: string) => {
        return filter(customFieldsFilterList, ['Type', customFieldType]).map(
            ({ Type, FieldName }: DynamicObject) => {
                const customFieldKey = `${customFieldIndicator}${Type}--${FieldName}`;
                return (
                    <Col span={12} key={customFieldKey}>
                        <FormItem label={FieldName}>
                            {getFieldDecorator(customFieldKey, {
                                initialValue: get(
                                    widgetDetails,
                                    customFieldKey
                                ),
                            })(
                                <Input
                                    placeholder={FieldName}
                                    readOnly={formDisabled}
                                />
                            )}
                        </FormItem>
                    </Col>
                );
            }
        );
    };

    /**
     * Function that populates the Customer custom fields section.
     */
    const populateCustomerCustomFieldsSection = () => {
        const customerCustomFields = populateCustomFieldsFilters(
            CUSTOM_FIELD_TYPES.CUSTOMER
        );
        if (!isEmpty(customerCustomFields)) {
            return (
                <>
                    <Divider />

                    <Row>
                        <Col>
                            <Row>
                                <Col span={24}>
                                    <h3>
                                        Filter by{' '}
                                        {isOrgView
                                            ? 'customer'
                                            : toLower(customerLabel)}{' '}
                                        custom fields
                                    </h3>
                                </Col>
                            </Row>
                            <Row gutter={10}>{customerCustomFields}</Row>
                        </Col>
                    </Row>
                </>
            );
        }

        return null;
    };

    /**
     * Function that populates the Invoice custom fields section.
     */
    const populateInvoiceCustomFieldsSection = () => {
        const invoiceCustomFields = populateCustomFieldsFilters(
            CUSTOM_FIELD_TYPES.INVOICE
        );
        if (!isEmpty(invoiceCustomFields)) {
            return (
                <>
                    <Divider />
                    <Row>
                        <Col>
                            <Row>
                                <Col span={24}>
                                    <h3>Filter by invoice custom fields</h3>
                                </Col>
                            </Row>
                            <Row gutter={10}>{invoiceCustomFields}</Row>
                        </Col>
                    </Row>
                </>
            );
        }

        return null;
    };

    return (
        <Row>
            <Col>
                <Row>
                    <Col span={24}>
                        <FormItem label="Filter on state of open invoices">
                            {getFieldDecorator('invoiceOpenState', {
                                initialValue:
                                    get(widgetDetails, 'invoiceOpenState') ||
                                    get(ATBStateOptions, '0.value'),
                                rules: [
                                    {
                                        required: true,
                                        message: 'Open invoice state required!',
                                    },
                                ],
                            })(
                                <SelectReadonly
                                    readOnly={formDisabled}
                                    style={{ width: '100%' }}
                                    getPopupContainer={populatePopoverContainer(
                                        drawerRef
                                    )}
                                >
                                    {populateInvoiceStateSelectOptions()}
                                </SelectReadonly>
                            )}
                        </FormItem>
                    </Col>
                </Row>
                {populateCustomerCustomFieldsSection()}
                {populateInvoiceCustomFieldsSection()}
                {isOrgView && (
                    <OrganisationWidgetCommonFilters
                        widgetDetails={widgetDetails}
                        getFieldDecorator={getFieldDecorator}
                        getFieldValue={getFieldValue}
                        setFieldsValue={setFieldsValue}
                        drawerRef={drawerRef}
                    />
                )}
            </Col>
        </Row>
    );
};

export default OpenSupportTicketsWidgetFields;
